import { useQueryClient } from '@tanstack/react-query'
import { BookMarked, LockKeyhole, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { ApiError, acceptTerms, logout } from '../../lib/api.ts'
import { disablePush } from '../../lib/push.ts'
import { ME_KEY, useSession } from '../../lib/queries.ts'

/** "Sebelum mulai": explicit acceptance of Terms/Privacy/Disclaimer. The server forwards it to CloudPass, never blocking this click. */
export function ConsentPage() {
  const { data } = useSession()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')

  if (data?.state !== 'ok') return null
  const { me } = data
  if (!me.needsTerms) return <Navigate to="/" replace />

  const agree = async () => {
    setError('')
    setPending(true)
    try {
      await acceptTerms(me.termsVersion)
      await qc.invalidateQueries({ queryKey: ME_KEY })
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.code === 'terms_version_mismatch') await qc.invalidateQueries({ queryKey: ME_KEY })
      setError(err instanceof ApiError ? err.message : 'Terjadi masalah. Coba lagi nanti.')
    } finally {
      setPending(false)
    }
  }

  const leave = async () => {
    await disablePush()
    await logout().catch(() => {})
    await qc.invalidateQueries({ queryKey: ME_KEY })
    navigate('/masuk', { replace: true })
  }

  return (
    <main className="consent">
      <p className="eyebrow">Selamat datang</p>
      <h1 style={{ marginTop: 6 }}>Sebelum mulai</h1>
      <p className="muted" style={{ marginTop: 8 }}>Tiga hal singkat yang perlu kamu tahu tentang IqraPath.</p>

      <ul className="points">
        <li className="card">
          <span className="ic" aria-hidden="true"><ShieldCheck size={20} /></span>
          <div><b>Yang kami simpan</b><span>Emailmu, kemajuan belajar, dan catatan yang kamu tulis sendiri.</span></div>
        </li>
        <li className="card">
          <span className="ic" aria-hidden="true"><LockKeyhole size={20} /></span>
          <div><b>Catatanmu privat</b><span>Jurnal, muhasabah, dan cerita tidak dibaca tim kami, kecuali kamu yang melaporkannya.</span></div>
        </li>
        <li className="card">
          <span className="ic amber" aria-hidden="true"><BookMarked size={20} /></span>
          <div><b>Bahan renungan, bukan fatwa</b><span>Isinya merujuk terjemah Kemenag RI. Untuk hukum fikih, tanyakan ke ustadz.</span></div>
        </li>
      </ul>

      <p className="legal-links">
        <Link to="/legal/syarat">Syarat &amp; Ketentuan</Link>
        <Link to="/legal/privasi">Kebijakan Privasi</Link>
        <Link to="/legal/disclaimer">Disclaimer</Link>
      </p>

      <p className="error" role="alert">{error}</p>
      <button className="btn btn-primary btn-block" type="button" onClick={agree} disabled={pending} style={{ minHeight: 54 }}>
        {pending ? <><span className="spinner" aria-hidden="true" /> Menyimpan…</> : 'Setuju & lanjut'}
      </button>
      <button className="btn btn-ghost btn-block" type="button" onClick={leave} style={{ marginTop: 10 }}>Keluar</button>
      <p className="help" style={{ textAlign: 'center', marginTop: 12 }}>Dengan menekan “Setuju & lanjut”, kamu menyetujui ketiganya (versi {me.termsVersion}).</p>
    </main>
  )
}
